import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  IOnboardingDrugTestService,
  IOnboardingOption,
  IOnboardingServices,
  IOnboardingStep2Context,
} from "../../../Interfaces/IOnboardingWizardState";
import {
  applySecondaryCollectionChange,
  applyServiceAlcoholChange,
  applyServiceCollectionChange,
  applyServiceLabOptionChange,
  applyServiceMethodChange,
  applyServiceReasonChange,
  availableLabOptions,
  copyService,
  defaultLabService,
  derivedSiteLabs,
  isAlcoholOnly,
  isCollectOnly,
  isLabChosenByClient,
  isLabLocked,
  isLabPanelApplicable,
  isLabPanelRequired,
  isLabServiceLocked,
  isMethodEnabled,
  isOffered,
  isSyntheticAvailable,
  LAB_SERVICE_NONE,
  labServiceLockReason,
  labsForMethod,
  METHOD_ALCOHOL,
  METHOD_ORAL_FLUID,
  METHOD_URINE,
  newSecondary,
  newService,
  oppositeMethod,
  optionDescription,
  optionLabel,
  screeningDevicesFor,
  SERVICE_NAME_MAX_LENGTH,
  syntheticDisabledReason,
  validateService,
} from "./Step2.rules";
import { optionMenuContent, renderOptionValue } from "./OptionMenu";

type Props = {
  services: IOnboardingServices;
  onChange: (changes: Partial<IOnboardingServices>) => void;
  references: {
    step2TestReasons: IOnboardingOption[];
    step2ServiceMethods: IOnboardingOption[];
    step2Collections: IOnboardingOption[];
    step2LabOptions: IOnboardingOption[];
    step2LabServices: IOnboardingOption[];
    step2ScreeningDevices: IOnboardingOption[];
    step2LabPanels: IOnboardingOption[];
    step2Laboratories: IOnboardingOption[];
    step2AddOns: IOnboardingOption[];
  };
  context: IOnboardingStep2Context;
  frozen: boolean;
};

/**
 * The testing services (bug 6364, stories 6349, 6353, 6361; S2.3–S2.6, S2.12).
 *
 * One card per named service rather than seven fixed occasions per method: Imperans keeps the name
 * as the only discriminator for repeated (reason, method) pairs, and stores the screening device and
 * the lab panel per service. The matrix rulings are still enforced by what the controls offer —
 * Collect only simply does not offer a screen-dependent lab option, a locked lab service is not
 * selectable, a lab panel appears on urine only — never by a warning after the fact.
 *
 * A service on an unticked collection method stays in the list, greyed, so nothing captured is lost;
 * it is neither validated nor sent (bug 6371).
 */
const DrugTestServicesTable: React.FC<Props> = ({ services, onChange, references, context, frozen }) => {
  const [notice, setNotice] = useState<string>("");
  const list = services.drugTestServices || [];
  const labChosen = isLabChosenByClient(context);
  const siteLabs = derivedSiteLabs(context);
  const offeredLabOptionKeys = references.step2LabOptions.filter(isOffered).map((x) => x.key);

  const announce = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 3000);
  };

  const replace = (index: number, next: IOnboardingDrugTestService) =>
    onChange({ drugTestServices: list.map((service, i) => (i === index ? next : service)) });

  const addService = (method: string) => {
    const reason = references.step2TestReasons[0]?.key ?? null;
    onChange({ drugTestServices: [...list, newService(reason, method, references)] });
  };

  const remove = (index: number) => {
    onChange({ drugTestServices: list.filter((_, i) => i !== index) });
    announce("Service removed");
  };

  const duplicate = (index: number) => {
    const copy = copyService(list[index]);
    onChange({ drugTestServices: [...list.slice(0, index + 1), copy, ...list.slice(index + 1)] });
    announce(`Copied ${list[index].name || "service"}`);
  };

  /** A slot with nothing to choose, drawn as a read-only field so every card keeps the same grid. */
  const fixedField = (label: string, value: string, helperText?: string | null) => (
    <TextField fullWidth size="small" disabled label={label} value={value} helperText={helperText || undefined} />
  );

  const addable: { method: string; label: string; enabled: boolean; why: string }[] = [
    { method: METHOD_URINE, label: "Add urine service", enabled: services.urineEnabled, why: "Tick Urine above first." },
    { method: METHOD_ORAL_FLUID, label: "Add oral fluid service", enabled: services.oralFluidEnabled, why: "Tick Oral fluid above first." },
    { method: METHOD_ALCOHOL, label: "Add alcohol-only service", enabled: true, why: "" },
  ];

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Testing services ({list.length})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            One card per service Imperans will hold. The name is what Imperans shows, so two services
            on the same reason and method need different names.
          </Typography>
        </Box>

        {/* Confirmation laboratories (S2.11). Not a question on an Australian client: the laboratory
            follows each office's state, so it is shown derived rather than asked (task 6008). */}
        {labChosen ? (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {[
              [METHOD_URINE, services.urineEnabled, services.urineLab, "urineLab", "Urine confirmation laboratory"],
              [METHOD_ORAL_FLUID, services.oralFluidEnabled, services.oralFluidLab, "oralFluidLab", "Oral fluid confirmation laboratory"],
            ]
              .filter(([, enabled]) => Boolean(enabled))
              .map(([method, , lab, field, label]) => {
                const locked = isLabLocked(method as string, context);
                return (
                  <TextField
                    key={field as string}
                    select
                    size="small"
                    required
                    sx={{ minWidth: 240 }}
                    label={label as string}
                    disabled={frozen || locked}
                    value={(lab as string) || ""}
                    helperText={
                      locked
                        ? "New Zealand oral fluid is confirmed at CHL — not selectable."
                        : optionDescription(references.step2Laboratories, lab as string)
                    }
                    onChange={(e) => onChange({ [field as string]: e.target.value } as any)}
                    SelectProps={{ renderValue: renderOptionValue(references.step2Laboratories) }}
                  >
                    {labsForMethod(method as string, context).map((key) => (
                      <MenuItem key={key} value={key}>
                        {optionMenuContent(references.step2Laboratories, key)}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })}
          </Box>
        ) : (
          (services.urineEnabled || services.oralFluidEnabled) && (
            <Box sx={{ maxWidth: 420 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Confirmation laboratory
              </Typography>
              <Typography variant="body2">
                {siteLabs.length
                  ? siteLabs
                      .map((x) => `${optionLabel(references.step2Laboratories, x.lab)} (${x.state})`)
                      .join(" · ")
                  : "Not yet derived — no site address gives a state"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Follows each office's state: QLD at SNP, NSW at DHM, every other state at SafeWork.
                Add or correct a site address on step 3 to change it.
              </Typography>
            </Box>
          )
        )}
      </Box>

      {/* The rule strip restates the headline rules above the services (S2.13). */}
      <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="caption" display="block">
          <strong>Screen</strong> tests on site; <strong>Collect only</strong> sends the sample away
          unscreened, so the laboratory always screens and confirms, there is no screening device, and
          a urine sample needs a lab panel.
        </Typography>
        <Typography variant="caption" display="block">
          <strong>Collect only</strong> always goes to the laboratory (Always Lab), and cannot carry a synthetic
          cannabis screen. <strong>No Lab If Non-Neg</strong> keeps everything on site.
        </Typography>
        <Typography variant="caption" display="block">
          A <strong>secondary screen</strong> escalates to the opposite method, which is why it cannot
          be chosen. An <strong>alcohol-only</strong> service has no sample to route.
        </Typography>
      </Alert>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
        {addable.map((add) => (
          <Tooltip key={add.method} title={add.enabled ? "" : add.why}>
            <span>
              <Button size="small" variant="outlined" disabled={frozen || !add.enabled} onClick={() => addService(add.method)}>
                {add.label}
              </Button>
            </span>
          </Tooltip>
        ))}
        {notice && <Chip size="small" color="success" label={notice} />}
      </Box>

      {!list.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No testing services yet. Add one per service the client is buying — Pre employment Urine,
          Random Oral Fluid, an alcohol-only Random test, and so on.
        </Typography>
      )}

      {/* Twelve table columns outgrew even a 1920px screen, hiding Alcohol, Secondary and the row
          actions behind a scrollbar. Each service is a card instead: its eight fields sit on the same
          grid in every card, top-aligned so the explanations under a field never push its neighbours
          out of line, and the card's problem is stated once at its foot rather than under the name. */}
      {list.map((service, index) => {
        const inactive = !isMethodEnabled(services, service.method);
        const disabled = frozen || inactive;
        const alcoholOnly = isAlcoholOnly(service);
        const collectOnly = isCollectOnly(service.collection);
        const serviceLocked = isLabServiceLocked(service.collection, service.labOption);
        const lockReason = labServiceLockReason(service.collection, service.labOption);
        const syntheticReason = syntheticDisabledReason(service.method, service.collection);
        const devices = screeningDevicesFor(service.method, references.step2ScreeningDevices).filter(isOffered);
        const problem = inactive ? null : validateService(service, references);
        const escalationLabel = oppositeMethod(service.method) === METHOD_URINE ? "urine" : "oral fluid";

        return (
          <Paper
            key={service.id || index}
            elevation={0}
            sx={{ p: 2, mt: 1, border: "1px solid #eee", backgroundColor: inactive ? "#fafafa" : "#fff" }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                columnGap: 2,
                rowGap: 1.5,
                alignItems: "start",
                opacity: inactive ? 0.55 : 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                required
                label="Service name"
                disabled={disabled}
                value={service.name || ""}
                error={Boolean(problem && /service name/i.test(problem))}
                inputProps={{ maxLength: SERVICE_NAME_MAX_LENGTH }}
                onChange={(e) => replace(index, { ...service, name: e.target.value, nameEdited: true })}
                helperText={
                  service.nameEdited &&
                  !disabled && (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      variant="caption"
                      onClick={() => replace(index, applyServiceReasonChange({ ...service, nameEdited: false }, service.testReasonCode || "", references))}
                    >
                      Edited — reset to default name
                    </Link>
                  )
                }
              />

              <TextField
                select
                fullWidth
                size="small"
                label="Test reason"
                disabled={disabled}
                value={service.testReasonCode || ""}
                onChange={(e) => replace(index, applyServiceReasonChange(service, e.target.value, references))}
                SelectProps={{ renderValue: renderOptionValue(references.step2TestReasons) }}
              >
                {references.step2TestReasons.filter(isOffered).map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {optionMenuContent(references.step2TestReasons, option.key)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                label="Method"
                disabled={frozen}
                value={service.method || ""}
                onChange={(e) => replace(index, applyServiceMethodChange(service, e.target.value, references))}
                SelectProps={{ renderValue: renderOptionValue(references.step2ServiceMethods) }}
              >
                {references.step2ServiceMethods.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {optionMenuContent(references.step2ServiceMethods, option.key)}
                  </MenuItem>
                ))}
              </TextField>

              {alcoholOnly ? (
                fixedField("Screening device", "No sample")
              ) : collectOnly ? (
                fixedField("Screening device", "Not screened on site")
              ) : (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Screening device"
                  disabled={disabled}
                  value={service.screeningDevice || ""}
                  onChange={(e) => replace(index, { ...service, screeningDevice: e.target.value })}
                  SelectProps={{ renderValue: renderOptionValue(references.step2ScreeningDevices) }}
                >
                  {devices.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {optionMenuContent(references.step2ScreeningDevices, option.key)}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {alcoholOnly ? (
                fixedField("Collection", "—")
              ) : (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Collection"
                  disabled={disabled}
                  value={service.collection || ""}
                  onChange={(e) => replace(index, applyServiceCollectionChange(service, e.target.value, references))}
                  SelectProps={{ renderValue: renderOptionValue(references.step2Collections) }}
                  helperText={optionDescription(references.step2Collections, service.collection)}
                >
                  {references.step2Collections.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {optionMenuContent(references.step2Collections, option.key)}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {alcoholOnly ? (
                fixedField("Lab option", "—")
              ) : (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Lab option"
                  disabled={disabled}
                  value={service.labOption || ""}
                  onChange={(e) => replace(index, applyServiceLabOptionChange(service, e.target.value))}
                  SelectProps={{ renderValue: renderOptionValue(references.step2LabOptions) }}
                  helperText={optionDescription(references.step2LabOptions, service.labOption)}
                >
                  {availableLabOptions(service.collection, offeredLabOptionKeys).map((key) => (
                    <MenuItem key={key} value={key}>
                      {optionMenuContent(references.step2LabOptions, key)}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {alcoholOnly ? (
                fixedField("Lab service", "—")
              ) : service.labService === LAB_SERVICE_NONE && serviceLocked ? (
                fixedField("Lab service", "No lab service", lockReason)
              ) : (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Lab service"
                  disabled={disabled || serviceLocked}
                  value={service.labService || ""}
                  onChange={(e) => replace(index, { ...service, labService: e.target.value, labServiceEdited: true })}
                  SelectProps={{ renderValue: renderOptionValue(references.step2LabServices) }}
                  helperText={
                    <>
                      {lockReason || optionDescription(references.step2LabServices, service.labService)}
                      {service.labServiceEdited && !serviceLocked && (
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          variant="caption"
                          sx={{ display: "block" }}
                          onClick={() =>
                            replace(index, {
                              ...service,
                              labService: defaultLabService(service.collection, service.labOption),
                              labServiceEdited: false,
                            })
                          }
                        >
                          Edited — reset to default
                        </Link>
                      )}
                    </>
                  }
                >
                  {references.step2LabServices
                    .filter((option) => option.key !== LAB_SERVICE_NONE)
                    .map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {optionMenuContent(references.step2LabServices, option.key)}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {/* Urine only; required once the sample is collected without screening (story 6353). */}
              {isLabPanelApplicable(service) ? (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Lab panel"
                  required={isLabPanelRequired(service)}
                  disabled={disabled}
                  value={service.labPanel || ""}
                  onChange={(e) => replace(index, { ...service, labPanel: e.target.value })}
                  SelectProps={{ renderValue: renderOptionValue(references.step2LabPanels) }}
                  helperText={isLabPanelRequired(service) ? "Required for a collect-only urine service." : undefined}
                >
                  {references.step2LabPanels.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {optionMenuContent(references.step2LabPanels, option.key)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                fixedField("Lab panel", "Urine only")
              )}
            </Box>

            {/* The add-ons on the left, the card's own actions on the right. */}
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mt: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2, opacity: inactive ? 0.55 : 1 }}>
                <Tooltip title={syntheticReason || ""}>
                  <span>
                    <FormControlLabel
                      label={<Typography variant="body2">Synthetic Cannabis</Typography>}
                      control={
                        <Checkbox
                          size="small"
                          disabled={disabled || !isSyntheticAvailable(service.method, service.collection)}
                          checked={service.syntheticCannabis}
                          onChange={(e) => replace(index, { ...service, syntheticCannabis: e.target.checked })}
                        />
                      }
                    />
                  </span>
                </Tooltip>
                <FormControlLabel
                  label={<Typography variant="body2">Alcohol</Typography>}
                  control={
                    <Checkbox
                      size="small"
                      disabled={disabled || alcoholOnly}
                      checked={service.alcohol}
                      onChange={(e) => replace(index, applyServiceAlcoholChange(service, e.target.checked, references))}
                    />
                  }
                />
                <Tooltip title={alcoholOnly ? "An alcohol-only service has no sample to escalate." : `Escalates to ${escalationLabel} — the opposite method.`}>
                  <span>
                    <FormControlLabel
                      label={<Typography variant="body2">Secondary screen</Typography>}
                      control={
                        <Checkbox
                          size="small"
                          disabled={disabled || alcoholOnly}
                          checked={Boolean(service.secondary)}
                          onChange={(e) => replace(index, { ...service, secondary: e.target.checked ? newSecondary() : null })}
                        />
                      }
                    />
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ whiteSpace: "nowrap" }}>
                <Button size="small" disabled={frozen} onClick={() => duplicate(index)}>
                  Copy
                </Button>
                <Button size="small" color="error" disabled={frozen} onClick={() => remove(index)}>
                  Remove
                </Button>
              </Box>
            </Box>

            {/* The escalation leg, restating itself in plain English (S2.5/S2.13). */}
            {service.secondary && !alcoholOnly && (
              <Box sx={{ borderLeft: "3px solid #ef7d00", backgroundColor: "#fdf7f0", p: 1, mt: 1, opacity: inactive ? 0.55 : 1 }}>
                <Typography variant="caption" fontWeight={600} display="block">
                  Secondary screen — {escalationLabel}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                  <TextField
                    select
                    size="small"
                    label="Collection"
                    sx={{ minWidth: 150 }}
                    disabled={disabled}
                    value={service.secondary.collection || ""}
                    onChange={(e) =>
                      replace(index, { ...service, secondary: applySecondaryCollectionChange(service.secondary!, e.target.value) })
                    }
                    SelectProps={{ renderValue: renderOptionValue(references.step2Collections) }}
                  >
                    {references.step2Collections.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {optionMenuContent(references.step2Collections, option.key)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Lab service"
                    sx={{ minWidth: 190 }}
                    disabled={disabled}
                    value={service.secondary.labService || ""}
                    onChange={(e) => replace(index, { ...service, secondary: { ...service.secondary!, labService: e.target.value } })}
                    SelectProps={{ renderValue: renderOptionValue(references.step2LabServices) }}
                  >
                    {references.step2LabServices.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {optionMenuContent(references.step2LabServices, option.key)}
                      </MenuItem>
                    ))}
                  </TextField>
                  {oppositeMethod(service.method) === METHOD_URINE && (
                    <FormControlLabel
                      label={<Typography variant="body2">Synthetic Cannabis</Typography>}
                      control={
                        <Checkbox
                          size="small"
                          disabled={disabled || isCollectOnly(service.secondary.collection)}
                          checked={service.secondary.syntheticCannabis}
                          onChange={(e) =>
                            replace(index, { ...service, secondary: { ...service.secondary!, syntheticCannabis: e.target.checked } })
                          }
                        />
                      }
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {`A second sample is taken by ${escalationLabel}, ${
                    isCollectOnly(service.secondary.collection) ? "collected without screening" : "screened on site"
                  }. ${optionDescription(references.step2LabServices, service.secondary.labService)}`}
                </Typography>
              </Box>
            )}

            {inactive ? (
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                Kept — {optionLabel(references.step2ServiceMethods, service.method)} is not ticked, so this is not sent.
              </Alert>
            ) : (
              problem && (
                <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                  {problem}
                </Alert>
              )
            )}
          </Paper>
        );
      })}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        {optionDescription(references.step2AddOns, "SyntheticCannabis")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {optionDescription(references.step2AddOns, "Secondary")}
      </Typography>
    </Paper>
  );
};

export default DrugTestServicesTable;
